import React, { useState, useEffect, useCallback } from "react";
import api from "../utils/api";
import Select from "react-select"; // Add this import

const KuralTable = (props) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  // State variables for total records, pagination, search and loading state.
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(12); // Increased for better card grid
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  // New state variables for athikaram filter
  const [athikaramList, setAthikaramList] = useState([]);
  const [selectedAthikaram, setSelectedAthikaram] = useState("");

  const handleEdit = (row) => {
    console.log("Edit row:", row);
    props.onEdit(row);
  };

  const handleDelete = (row) => {
    console.log("Delete row:", row);
    // Add delete functionality here
  };

  function getStartPosition(pageNumber, recordsPerPage) {
    return (pageNumber - 1) * recordsPerPage;
  }

  // Fetch all available athikaram values
  const fetchAthikaramList = useCallback(async () => {
    try {
      const response = await api.get(
        "/api/kural/index.php?distinct_athikarams=true"
      );
      // Check if response.data is an array (the API returns an array of objects)
      if (Array.isArray(response.data)) {
        setAthikaramList(response.data);
      } else if (response.data && Array.isArray(response.data.athikaram)) {
        // Fallback for the old format if needed
        setAthikaramList(response.data.athikaram);
      }
      console.log("Athikaram list:", response.data);
    } catch (error) {
      console.error("Error fetching athikaram list:", error);
    }
  }, []);

  const fetchKurals = useCallback(async () => {
    setLoading(true);
    // Build query parameters. Adjust parameter names if your backend expects different keys.
    const queryParams = new URLSearchParams();
    queryParams.append("start", getStartPosition(page, perPage));
    queryParams.append("length", perPage);
    queryParams.append("search[value]", search);

    // Add athikaram filter if selected
    if (selectedAthikaram) {
      queryParams.append("athikaram", selectedAthikaram);
    }

    try {
      const response = await api.get(
        `/api/kural/kuralTable.php?${queryParams}`
      );
      console.log("Response:", response.data);
      setData(Array.isArray(response.data.data) ? response.data.data : []);
      setTotalRows(response.data.recordsFiltered);
    } catch (error) {
      console.error("Error fetching kurals:", error);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [page, perPage, search, selectedAthikaram]);

  useEffect(() => {
    fetchAthikaramList();
  }, [fetchAthikaramList]);

  useEffect(() => {
    fetchKurals();
  }, [fetchKurals, page, perPage, search, selectedAthikaram]);

  // Handle athikaram selection change
  const handleAthikaramChange = (option) => {
    setSelectedAthikaram(option ? option.value : "");
    setPage(1); // Reset to first page when filter changes
  };

  // Calculate total pages
  const totalPages = Math.ceil(totalRows / perPage);

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5; // Show at most 5 page numbers

    if (totalPages <= maxPagesToShow) {
      // If total pages are less than max to show, display all pages
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      // Always include first page
      pageNumbers.push(1);

      // Calculate start and end of page numbers to show
      let startPage = Math.max(2, page - 1);
      let endPage = Math.min(totalPages - 1, page + 1);

      // Adjust if we're at the beginning or end
      if (page <= 2) {
        endPage = 4;
      } else if (page >= totalPages - 1) {
        startPage = totalPages - 3;
      }

      // Add ellipsis if needed
      if (startPage > 2) {
        pageNumbers.push("...");
      }

      // Add middle pages
      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }

      // Add ellipsis if needed
      if (endPage < totalPages - 1) {
        pageNumbers.push("...");
      }

      // Always include last page
      pageNumbers.push(totalPages);
    }

    return pageNumbers;
  };

  return (
    <div className="container mx-auto px-4">
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        {/* Athikaram Dropdown */}
        <div className="w-full md:w-1/3">
          <label
            htmlFor="athikaram"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Filter by Athikaram
          </label>
          <Select
            id="athikaram"
            className="w-full"
            placeholder="Search or select an athikaram..."
            options={athikaramList.map((item) => ({
              value: typeof item === "object" ? item.athikaram : item,
              label:
                typeof item === "object"
                  ? `${item.athikaram} (${item.athikaram_no})`
                  : item,
            }))}
            value={
              selectedAthikaram
                ? {
                    value: selectedAthikaram,
                    label: athikaramList.find(
                      (item) =>
                        (typeof item === "object" ? item.athikaram : item) ===
                        selectedAthikaram
                    )
                      ? typeof athikaramList.find(
                          (item) =>
                            (typeof item === "object"
                              ? item.athikaram
                              : item) === selectedAthikaram
                        ) === "object"
                        ? `${selectedAthikaram} (${
                            athikaramList.find(
                              (item) => item.athikaram === selectedAthikaram
                            ).athikaram_no
                          })`
                        : selectedAthikaram
                      : selectedAthikaram,
                  }
                : null
            }
            onChange={(option) => handleAthikaramChange(option)}
            isClearable={true}
            isSearchable={true}
            styles={{
              control: (base) => ({
                ...base,
                padding: "0.25rem",
                borderColor: "#d1d5db",
                borderRadius: "0.375rem",
                backgroundColor: "#fff",
              }),
              menu: (base) => ({
                ...base,
                marginTop: "0.25rem",
                borderRadius: "0.375rem",
              }),
            }}
          />
        </div>

        {/* Search input */}
        <div className="w-full md:w-2/3">
          <label
            htmlFor="search"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Search Kurals
          </label>
          <input
            id="search"
            type="text"
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            placeholder="Search kurals..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        </div>
      </div>

      {/* Active filters display */}
      {selectedAthikaram && (
        <div className="mb-4 flex items-center">
          <span className="text-sm text-gray-600 mr-2">Active filter:</span>
          <span className="bg-indigo-100 text-indigo-800 text-xs font-medium px-2.5 py-0.5 rounded flex items-center">
            {selectedAthikaram}
            <button
              onClick={() => handleAthikaramChange(null)}
              className="ml-1.5 text-indigo-600 hover:text-indigo-900"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-3 w-3"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </span>
        </div>
      )}

      {/* Loading indicator */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
        </div>
      ) : (
        <>
          {/* Card grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {data.length > 0 ? (
              data.map((kural) => (
                <div
                  key={kural.id}
                  className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200 hover:shadow-lg transition-shadow duration-300"
                >
                  <div className="p-4">
                    <div className="flex justify-between items-start mb-2">
                      <span className="bg-indigo-100 text-indigo-800 text-l font-semibold px-2.5 py-0.5 rounded">
                        {kural.kural_no}
                      </span>
                      <span className="text-sm text-gray-500">
                        {kural.athikaram}
                      </span>
                    </div>

                    <div className="mb-4 text-lg font-medium text-gray-800 border-b pb-2">
                      <div>{kural.kural_line1}</div>
                      <div>{kural.kural_line2}</div>
                    </div>

                    <div className="text-sm text-gray-600 mb-4">
                      {kural.meaning_ta_mu_va && (
                        <p className="mb-2">
                          <span className="font-semibold">மு.வா:</span>{" "}
                          {kural.meaning_ta_mu_va}
                        </p>
                      )}
                      {kural.meaning_ta_salaman && (
                        <p>
                          <span className="font-semibold">சாலமன்:</span>{" "}
                          {kural.meaning_ta_salaman}
                        </p>
                      )}
                    </div>

                    <div className="flex justify-end space-x-2 pt-2 border-t">
                      <button
                        onClick={() => handleEdit(kural)}
                        className="p-1 text-blue-600 hover:text-blue-800"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                          />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleDelete(kural)}
                        className="p-1 text-red-600 hover:text-red-800"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="col-span-3 text-center py-10 text-gray-500">
                No kurals found matching your search criteria.
              </div>
            )}
          </div>

          {/* Custom Pagination */}
          <div className="flex justify-between items-center mt-8 mb-4">
            <div className="text-sm text-gray-700">
              Showing{" "}
              <span className="font-medium">
                {data.length > 0 ? getStartPosition(page, perPage) + 1 : 0}
              </span>{" "}
              to{" "}
              <span className="font-medium">
                {Math.min(getStartPosition(page, perPage) + perPage, totalRows)}
              </span>{" "}
              of <span className="font-medium">{totalRows}</span> results
            </div>

            <div className="flex items-center space-x-1">
              {/* Rows per page selector */}
              <select
                className="border border-gray-300 rounded-md text-sm px-2 py-1 mr-4"
                value={perPage}
                onChange={(e) => {
                  setPerPage(Number(e.target.value));
                  setPage(1);
                }}
              >
                {[12, 24, 36, 48].map((size) => (
                  <option key={size} value={size}>
                    {size} per page
                  </option>
                ))}
              </select>

              {/* Previous page button */}
              <button
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                className={`px-3 py-1 rounded-md ${
                  page === 1
                    ? "text-gray-400 cursor-not-allowed"
                    : "text-gray-700 hover:bg-gray-100"
                }`}
              >
                Previous
              </button>

              {/* Page numbers */}
              {getPageNumbers().map((pageNum, index) => (
                <button
                  key={index}
                  onClick={() =>
                    typeof pageNum === "number" && setPage(pageNum)
                  }
                  disabled={pageNum === "..."}
                  className={`px-3 py-1 rounded-md ${
                    pageNum === "..." ? "cursor-default" : ""
                  } ${
                    pageNum === page
                      ? "bg-indigo-600 text-white"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  {pageNum}
                </button>
              ))}

              {/* Next page button */}
              <button
                onClick={() => setPage(page + 1)}
                disabled={page === totalPages}
                className={`px-3 py-1 rounded-md ${
                  page === totalPages
                    ? "text-gray-400 cursor-not-allowed"
                    : "text-gray-700 hover:bg-gray-100"
                }`}
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default KuralTable;
